<script setup>
import { Icon } from '@iconify/vue';
import DashboardWidgetCard from './DashboardWidgetCard.vue';

defineProps({
    comunicados: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    error: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['openDetail', 'reload']);

const formatLocalDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
};
</script>

<template>
    <DashboardWidgetCard
        title="Comunicados Institucionales"
        subtitle="Avisos, circulares y notas informativas"
        icon="tabler:speakerphone"
        iconColor="indigo"
        :badge="comunicados.length ? comunicados.length : null"
        badgeColor="indigo"
        :loading="loading"
        :error="error"
        :empty="!comunicados.length"
        emptyMessage="No hay comunicados vigentes en este momento."
        @reload="emit('reload')"
    >
        <div class="space-y-2.5">
            <div
                v-for="item in comunicados"
                :key="item.id"
                class="flex items-center justify-between p-3.5 rounded-xl border border-gray-100 dark:border-slate-800 bg-gray-50/70 dark:bg-slate-800/40 hover:bg-white dark:hover:bg-slate-800 transition hover:shadow-xs group"
            >
                <div class="flex items-start min-w-0 pr-3 gap-3">
                    <div
                        class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 shadow-xs"
                        :class="item.requires_acceptance ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400'"
                    >
                        <Icon v-if="item.file_type === 'image'" icon="tabler:photo" class="w-5 h-5" />
                        <Icon v-else-if="item.file_type === 'pdf'" icon="tabler:file-type-pdf" class="w-5 h-5 text-rose-500" />
                        <Icon v-else icon="tabler:speakerphone" class="w-5 h-5" />
                    </div>

                    <div class="truncate">
                        <div class="flex items-center gap-2">
                            <span class="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200 truncate group-hover:text-indigo-600 transition">
                                {{ item.title }}
                            </span>
                            <span
                                v-if="item.requires_acceptance && !item.is_accepted"
                                class="shrink-0 px-1.5 py-0.5 text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 rounded"
                            >
                                Pendiente
                            </span>
                            <span
                                v-else-if="item.is_accepted"
                                class="shrink-0 px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded"
                            >
                                Leído
                            </span>
                        </div>

                        <div class="flex flex-wrap items-center gap-2 mt-0.5">
                            <span v-if="item.end_date" class="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 font-normal">
                                <Icon icon="tabler:calendar-check" class="w-3.5 h-3.5 text-indigo-500" />
                                Vigente: {{ formatLocalDate(item.end_date) }}
                            </span>
                            <span v-if="item.description" class="text-[11px] text-gray-400 truncate max-w-[220px]">
                                · {{ item.description }}
                            </span>
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    @click="emit('openDetail', item)"
                    class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white rounded-xl text-xs font-semibold shadow-xs transition"
                    title="Ver comunicado completo"
                >
                    <Icon icon="tabler:eye" class="w-4 h-4" />
                    <span class="hidden xs:inline">Ver</span>
                </button>
            </div>
        </div>
    </DashboardWidgetCard>
</template>
